import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import moment from "moment/moment";
import FactorCalendarTypeEnum from "@/enums/FactorCalendarTypeEnum";

/** Classe de fonctions utilitaires liées aux objets */
@Component
export class FactorUtils extends Vue {
    /**
     *  Transformation d'une date de type Date en type Moment en string
     * @param date
     * @param start
     * @private
     */
    protected getMomentDateToString(date, start: boolean = true): string {
        //console.debug('FactorUtils.ts - getMomentDateToString - date', date);
        let dateMoment: any = undefined;
        if (typeof (date) == 'string')
            dateMoment = moment(date);
        else
            dateMoment = date;

        dateMoment.locale(this.$store.getters['SessionStore/GetLanguage']);

        let minDate = moment.utc("0001-01-02"); // minimum value as per UTC
        let maxDate = moment.utc("9999-12-30"); // minimum value as per UTC
        if (!moment.utc(dateMoment).isAfter(minDate) || !moment.utc(dateMoment).isBefore(maxDate)) {
            return start ? '-∞' : '+∞';
        }

        if (moment.isMoment(dateMoment)) {
            return dateMoment.format("DD/MM/YYYY HH:mm");
        } else if (typeof dateMoment == 'object') {
            return ('0' + dateMoment.getDay()).slice(-2) + '/' + ('0' + (dateMoment.getMonth() + 1)).slice(-2)
                + '/' + dateMoment.getFullYear() + ' '
                + ('0' + dateMoment.getHours()).slice(-2) + ':' + ('0' + dateMoment.getMinutes()).slice(-2);
        }
    }

    /**
     * Transformation d'une date de type Date en type Moment
     * @param date
     * @param minMaxUndefined permet d'indiquer de retourner la valeur undefined s'il s'agit de la valeur minimum ou maximum
     * @private
     */
    protected getMomentDate(date, minMaxUndefined: boolean = false): moment.Moment {
        if (date != undefined) {
            console.debug('FactorUtils.ts - getMomentDate - date', date, typeof(date));
            let dateMoment: moment.Moment = undefined;
            let dateTemp = date;
            if (dateTemp instanceof Date) {
                dateTemp = dateTemp.toISOString();
            }
            console.debug('FactorUtils.ts - getMomentDate - dateTemp : %O', dateTemp);

            if (typeof (dateTemp) == 'string')
                dateMoment = moment(dateTemp);
            else if (moment.isMoment(dateTemp)) {
                dateMoment = dateTemp;
            }
            console.debug('FactorUtils.ts - getMomentDate - dateMoment : %O', dateMoment);

            if (minMaxUndefined) {
                let minDate = moment.utc("0001-01-02"); // minimum value as per UTC
                let maxDate = moment.utc("9999-12-30"); // minimum value as per UTC
                if (!moment.utc(dateMoment).isAfter(minDate) || !moment.utc(dateMoment).isBefore(maxDate)) {
                    console.debug('FactorUtils.ts - getMomentDate - return', undefined);
                    return undefined;
                }
            }
            //console.debug('FactorUtils.ts - getMomentDate - return', dateMoment, moment.isMoment(dateMoment));
            return dateMoment;
        }
        //console.debug('FactorUtils.ts - getMomentDate - return', undefined);
        return undefined;
    }

    /**
     * Récupération de la date sélectionnée - Mise au bon format
     * @param calendarType
     * @param startPeriod
     * @param endPeriod
     * @protected
     */
    protected getSelectedDate(calendarType: FactorCalendarTypeEnum,
                              startPeriod: moment.Moment, endPeriod: moment.Moment): string {
        if (startPeriod != undefined && endPeriod != undefined) {
            console.debug('FactorUtils.ts - getSelectedDate - Month - startPeriod : %O - endPeriod : %0',
                startPeriod, endPeriod);
            startPeriod.locale(this.$store.getters['SessionStore/GetLanguage']);
            endPeriod.locale(this.$store.getters['SessionStore/GetLanguage']);
            switch (calendarType) {
                case FactorCalendarTypeEnum.Semester :
                    return startPeriod.format('MMMM YYYY') + ' - ' + endPeriod.format('MMMM YYYY');
                case FactorCalendarTypeEnum.Month :
                    return startPeriod.format('MMMM YYYY');
                case FactorCalendarTypeEnum.Week :
                    return startPeriod.format('dddd D MMMM YYYY') + ' - ' + endPeriod.subtract('hours', 1).format('dddd D MMMM YYYY');
                case FactorCalendarTypeEnum.Day :
                    return startPeriod.format('dddd D MMMM YYYY');
            }
        }
    }

    /**
     * Retour les valeurs minimum et maximum des données
     * @param datas
     * @param startPeriod
     * @param endPeriod
     * @protected
     */
    protected getMinMax(datas: { data: any[], outerData: any[] },
                        startPeriod: moment.Moment, endPeriod: moment.Moment): { min: number, max: number } {

        console.debug('FactorUtils.ts - getMinMax - datas : %O - startPeriod : %O - endPeriod :%O',
            datas, startPeriod, endPeriod);

        /** Calcul des valeurs min et max */
        if (datas.data != undefined && datas.data.length > 0) {
            let maxValue = Math.max(...datas.data.map( a=> a.maxValue));
            let minValue = Math.min(...datas.data.map( a=> a.minValue));

            console.debug('FactorUtils.ts - getMinMax - maxValue : %O - minValue : %O', maxValue, minValue);
            return {min: minValue, max: maxValue};
        }
        console.debug('FactorUtils.ts - getMinMax - maxValue : %O - minValue : %O', undefined, undefined);
        return {min: undefined, max: undefined};
    }

    /**
     * Retourne le numéro de la date
     * @param date
     * @param level
     * @protected
     */
    protected getDateToNumber(date: moment.Moment, level: string): number {
        switch (level) {
            case 'year':
                return date.year();
            case 'month':
                return date.year() * 100 + date.month();
            case 'day':
                return date.year() * 10000 + (date.month() + 1) * 100 + date.date();
            case 'hour':
                return date.year() * 1000000 + (date.month() + 1) * 10000 + date.date() * 100 + date.hour();
            case 'minute':
                return date.year() * 100000000 + (date.month() + 1) * 1000000 + date.date() * 10000 + date.hour() * 100 + date.minute();
        }
    }

    /**
     * Retourne la date en fonction du numéro de date
     * @param noDate
     * @param level
     * @protected
     */
    protected getNumberToDate(noDate: number, level: string): moment.Moment {
        switch (level) {
            case 'year':
                return moment(new Date(noDate));
            case 'month':
                return moment(new Date(Math.trunc(noDate / 100), Math.trunc((noDate - Math.trunc(noDate / 100) * 100)) - 1));
            case 'day':
                return moment(new Date(Math.trunc(noDate / 10000), Math.trunc((noDate - Math.trunc(noDate / 10000) * 10000) / 100) - 1,
                    noDate - Math.trunc(noDate / 100) * 100));
            case 'hour':
                return moment(new Date(Math.trunc(noDate / 1000000), Math.trunc((noDate - Math.trunc(noDate / 1000000) * 1000000) / 10000) - 1,
                    noDate - Math.trunc(noDate / 10000) * 10000, noDate - Math.trunc(noDate / 100) * 100));
            case 'minute':
                return moment(new Date(Math.trunc(noDate / 100000000), Math.trunc((noDate - Math.trunc(noDate / 100000000) * 100000000) / 1000000) - 1,
                    noDate - Math.trunc(noDate / 1000000) * 1000000, noDate - Math.trunc(noDate / 10000) * 10000,
                    noDate - Math.trunc(noDate / 100) * 100));
        }
    }
}